import {
	PageHeader,
	Button,
	InfiniteLoadTrigger,
	ItemRowHeader,
	useLayout,
	useNotifications,
	RichEmptyState,
	ContentSidebarLayout,
	Colour,
	ColourStateSolid,
	ContentLoader,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { ItemRowHeaderCell } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/Cells/ItemRowHeaderCell';
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faKey } from '@fortawesome/pro-solid-svg-icons/faKey';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import {
	disableUser,
	GetItemCounts,
	sendPasswordResetEmail,
	useInfiniteAccountUsers,
	UserDataType,
	UsersUpdateType,
	useUpdateAccountUsers,
} from '@ukpostbox/client-data';
import { EndpointError } from '../../../_Global/Components/EndpointError';
import SendsNavigation from '../../../SendMail/Components/Shared/Controls/SendsNavigation';
import SendsUserItem from '../Components/SendsUserItem';
import CreateUserModal from '../Components/Modals/CreateUserModal';
import ConfirmDeleteUserModal from '../Components/Modals/ConfirmDeleteUserModal';
import { useSite } from '../../../_Global/Providers/SiteInterfaceProvider';

function Users() {
	const { t } = useTranslation();
	const { AddNotification } = useNotifications();
	const { isTiny, isSmall, isMedium } = useLayout();
	const { currentLocale, updatePageMeta } = useSite();

	const { status, data, error, isSuccess, isFetchingNextPage, fetchNextPage, hasNextPage, isStale, isRefetching } =
		useInfiniteAccountUsers();

	const [createUserOpen, setCreateUserOpen] = useState<boolean>(false);
	const [activeUser, setActiveUser] = useState<any>({ isOpen: false, user: null });
	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const updateUsers = useUpdateAccountUsers();

	const sendsUsersRef = useRef<any>(null);
	const sendMailNavigationRef = useRef<any>(null);

	const SendPasswordReset = (userId: number) => {
		sendPasswordResetEmail(userId).then((r) => {
			if (r) {
				AddNotification({
					title: t('results.passwordResetSent'),
					icon: faKey,
					colour: Colour.Success,
				});
			} else {
				AddNotification({
					title: t('results.passwordResetFailed'),
					icon: faKey,
					colour: Colour.Alert,
				});
			}
		});
	};

	const ShowDeleteConfirmation = (user: UserDataType) => {
		setActiveUser({ user, isOpen: true });
	};

	const HandleDisable = async (userId: number, disabled: boolean) => {
		disableUser(userId, disabled).then((r) => {
			updateUsers.mutate({
				updateType: UsersUpdateType.DISABLE,
				newData: userId,
			});
		});
	};

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data as any));
		}
	}, [data]);

	useEffect(() => {
		updatePageMeta({
			title: t('sends.users.title'),
		});
	}, [currentLocale]);

	return (
		<ContentSidebarLayout
			ref={sendsUsersRef}
			navigationComponent={
				<SendsNavigation
					ref={sendMailNavigationRef}
					title={t('sends.users.title')}
					addonContent={
						isSuccess &&
						(isTiny || isSmall || isMedium) && (
							<Button
								colour={ColourStateSolid.Primary}
								onClick={() => setCreateUserOpen(true)}
								squared
								title={t('sends.users.actions.add')}
								icon={faUser}
							/>
						)
					}
				/>
			}
			extraContentClass='flex-column'
		>
			{!isTiny && !isSmall && !isMedium && (
				<PageHeader title={t('sends.users.title')} secondaryHeader extraClass='section-page-header'>
					{isSuccess && (
						<Button
							colour={ColourStateSolid.Primary}
							onClick={() => setCreateUserOpen(true)}
							squared
							title={t('sends.users.actions.add')}
							icon={faUser}
						/>
					)}
				</PageHeader>
			)}

			{!isTiny && !isSmall && (
				<ItemRowHeader title='Sends Users' sticky sidePadding extraClass='sends-users-row'>
					<ItemRowHeaderCell>{t('sends.users.headings.name')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.users.headings.email')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.users.headings.actions')}</ItemRowHeaderCell>
					<ItemRowHeaderCell>{t('sends.users.headings.active')}</ItemRowHeaderCell>
				</ItemRowHeader>
			)}

			{status === 'pending' ? (
				<ContentLoader childHeight='5em' />
			) : status === 'error' ? (
				<EndpointError {...error} stacked extraClass='m-md' />
			) : (
				<>
					{data &&
						data.pages.map((group) =>
							group.data.map((entry) => (
								<SendsUserItem
									{...entry}
									key={entry.userId}
									disableCallback={(checked: boolean) => HandleDisable(entry.userId, checked)}
									passwordResetCallback={() => SendPasswordReset(entry.userId)}
									deleteCallback={() => ShowDeleteConfirmation(entry)}
								/>
							)),
						)}

					{itemCounts.total === 0 && (
						<RichEmptyState
							title={t('sends.users.noData.title')}
							description={t('sends.users.noData.description')}
							media={<FontAwesomeIcon icon={faUsers} size='3x' />}
							addon={
								<Button
									title={t('sends.profiles.actions.getStarted')}
									colour={ColourStateSolid.Primary}
									onClick={() => setCreateUserOpen(true)}
								/>
							}
						/>
					)}

					{itemCounts.total > 0 && (
						<InfiniteLoadTrigger
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isStale={isStale}
							isRefetching={isRefetching}
							isFetchingNextPage={isFetchingNextPage}
						/>
					)}
				</>
			)}

			<CreateUserModal isOpen={createUserOpen} closeCallback={() => setCreateUserOpen(false)} />

			<ConfirmDeleteUserModal
				user={activeUser.user}
				isOpen={activeUser.isOpen}
				closeCallback={() => setActiveUser({ ...activeUser, isOpen: false })}
			/>
		</ContentSidebarLayout>
	);
}

export default Users;
